import styled from 'styled-components';
import React, { ReactElement } from 'react';
import { forDesktop, forIE, forMidDesktop } from '@westwing/ui-kit/breakPoints';
import { body2Bold, body2, caption1Bold, caption1 } from '@westwing/ui-kit/typography';
import useBellaTranslator from '../../hooks/useTranslator';
import { ProductInterface } from '../../redux/products/types';
import useListingPageUrlState from '../ListingPage/hooks/useListingPageUrlState';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import { BellaTestIds } from '../../../common/qaIds';
import PriceRenderer from './PriceRenderer';
import { SoldOutBadge } from './TextBadges';
import { useSizeSuffix, getSizeSuffix } from './size-suffix-utils';
import { textLinesLimitClipCss } from 'Client/components/Generic/Text/StyledText';
import EnergyEfficiencyBadge from 'Client/components/Product/EnergyEfficiencyBadge';

const StyledProductInfoBox = styled.div`
    grid-row: 3/4;
    grid-column: 1/4;

    padding: 12px 15px;
    -webkit-font-smoothing: antialiased;
    -moz-font-smoothing: antialiased;
    /* stylelint-disable-next-line property-no-unknown */
    font-smoothing: antialiased;
    text-align: left;
    color: ${({ theme }) => theme.colors.charcoal100};
    background: ${({ theme }) => theme.colors.white};

    ${forIE`
        -ms-grid-row : 3;
        -ms-grid-row-span : 1;

        -ms-grid-column : 1;
        -ms-grid-column-span : 3;
    `}
`;

export const StyledTitle = styled.h2<{ isHeroProduct: boolean }>`
    ${caption1Bold}
    margin: 0;
    max-width: ${({ isHeroProduct }) => (isHeroProduct ? '75%' : '100%')};
    ${textLinesLimitClipCss(2)}
    line-height: 1.25em;
    max-height: 3em;

    ${forMidDesktop`
        ${body2Bold}
        line-height: 16.8px;
        letter-spacing: 0.2px;
    `}
`;

export const StyledBrandText = styled.div`
    ${caption1}

    ${forMidDesktop`
        ${body2}
        line-height: 16.8px;
        letter-spacing: 0.2px;
    `}
`;

const StyledPriceRenderer = styled(PriceRenderer)``;

export const StyledPrice = styled.div`
    ${caption1}
    display: inline-block;
    margin-top: 7px;
    line-height: 1.5;
    vertical-align: bottom;

    ${forMidDesktop`
        ${body2}
        line-height: 16.8px;
        letter-spacing: 0.2px;
    `}

    ${StyledPriceRenderer}:not(:last-child) {
        padding-right: 10px;
    }
`;

const BadgeWrapper = styled.div`
    padding-bottom: 7px;
    ${forDesktop`
        padding-bottom: 8.2px;
    `}
`;

export const DESC_PRICE_SORT_VALUE = 'facet_price';
export const DESC_PRICE_ORDER_VALUE = 'desc';

export interface ProductProps {
    product: ProductInterface;
    isHeroProduct: boolean;
    discountColor?: string;
    handleEfficiencyBadgeClick?: (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
}

const ProductInfoBox: React.FC<ProductProps> = React.memo(
    ({ isHeroProduct, product, discountColor, handleEfficiencyBadgeClick }): ReactElement => {
        const {
            brand,
            name,
            price,
            originalPrice,
            simplesCount,
            blackFriday,
            cyberMonday,
            soldOut,
            energyEfficiencyLabel,
            energyEfficiencyColor,
        } = product;
        const featureToggles = useFeatureToggle();
        const isBlackFriday = blackFriday && featureToggles.isBlackFridayActive;
        const isCyberMonday = cyberMonday && featureToggles.isCyberMondayActive;
        const showSoldOutBadge = soldOut && (isBlackFriday || isCyberMonday);
        const shouldAddSizeSuffix = useSizeSuffix();

        const t = useBellaTranslator();
        const [urlState] = useListingPageUrlState();
        const {
            sortingParams: { order, sort },
        } = urlState;

        const buildPriceDescriptionString = (): string => {
            if (simplesCount > 1) {
                if (sort === DESC_PRICE_SORT_VALUE && order === DESC_PRICE_ORDER_VALUE) {
                    return t('up to');
                }
                return t('from');
            }

            return '';
        };

        const priceDescription = buildPriceDescriptionString();

        return (
            <StyledProductInfoBox>
                {showSoldOutBadge && (
                    <BadgeWrapper>
                        <SoldOutBadge />
                    </BadgeWrapper>
                )}
                <StyledTitle data-testid="product-title" isHeroProduct={isHeroProduct}>
                    {shouldAddSizeSuffix ? `${name} ${getSizeSuffix(urlState)}` : name}
                </StyledTitle>
                {brand && <StyledBrandText>{brand}</StyledBrandText>}
                <StyledPrice>
                    {priceDescription}{' '}
                    <StyledPriceRenderer
                        testId={BellaTestIds.productPrice}
                        price={price}
                        originalPrice={originalPrice}
                        discountColor={discountColor}
                    />
                    <EnergyEfficiencyBadge
                        label={energyEfficiencyLabel}
                        color={energyEfficiencyColor}
                        onClick={handleEfficiencyBadgeClick}
                    />
                </StyledPrice>
            </StyledProductInfoBox>
        );
    }
);

export default ProductInfoBox;
